import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Bot, Send, Trash2, Loader } from 'lucide-react';

const LIEN_CREDITS = 'https://app.innovactive360.cloud/achats/nouveau';

const SUGGESTIONS = [
  'Comment structurer un plan de formation efficace ?',
  'Explique-moi le concept de gestion de projet agile.',
  'Quelles sont les meilleures pratiques pour apprendre en ligne ?',
  'Comment améliorer ma prise de notes pendant un cours ?',
  'Que faire si je bloque sur un exercice ?',
];

const AIAssistant = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Crédits IA (solde commun à toute la plateforme) : 1 par question, affichage seulement
  const [credits, setCredits] = useState(null);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    axios.get('/api/ai/credits')
      .then(({ data }) => { if (data?.credits) setCredits(data.credits); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;

    const userMsg = { role: 'user', content };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setError(null);
    setLoading(true);

    try {
      // withCredentials global (axios.defaults) envoie automatiquement le cookie HttpOnly
      const { data } = await axios.post('/api/ai/chat', { messages: nextMessages });

      if (data.credits) setCredits(data.credits);
      if (data.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.content }]);
      } else {
        setError(data.error || 'Erreur inconnue');
      }
    } catch (err) {
      if (err.response?.data?.credits) setCredits(err.response.data.credits);
      const msg = err.response?.data?.error || err.message || 'Erreur de connexion';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setError(null);
  };

  if (!user) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Accès non autorisé</h1>
          <p className="text-gray-600">Vous devez être connecté pour accéder à l'assistant IA.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-text">Assistant IA</h1>
                <p className="text-gray-600 text-sm">Propulsé par Claude · Toujours disponible pour vous aider</p>
              </div>
            </div>
            {messages.length > 0 && (
              <button
                onClick={clearConversation}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Effacer
              </button>
            )}
          </div>
        </div>

        {/* Zone de conversation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col" style={{ height: '65vh' }}>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center gap-6">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
                  <Bot className="w-10 h-10 text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">Comment puis-je vous aider ?</h2>
                  <p className="text-gray-500 text-sm max-w-sm">
                    Posez n'importe quelle question sur vos cours, demandez des explications ou de l'aide pour vos exercices.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSend(s)}
                      className="text-left px-4 py-3 text-sm text-gray-700 bg-gray-50 hover:bg-primary/5 hover:text-primary border border-gray-200 rounded-lg transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' ? 'bg-primary' : 'bg-gray-100'
                    }`}>
                      {msg.role === 'user' ? (
                        <span className="text-white text-xs font-bold">
                          {user.firstName?.[0] || user.email?.[0] || 'U'}
                        </span>
                      ) : (
                        <Bot className="w-4 h-4 text-primary" />
                      )}
                    </div>
                    <div className={`max-w-[75%] ${msg.role === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                      <span className="text-xs text-gray-500">
                        {msg.role === 'user' ? 'Vous' : 'Assistant IA'}
                      </span>
                      <div className={`px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-primary text-white rounded-tr-sm'
                          : 'bg-gray-100 text-gray-900 rounded-tl-sm'
                      }`}>
                        {msg.content}
                      </div>
                    </div>
                  </div>
                ))}

                {loading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs text-gray-500">Assistant IA</span>
                      <div className="px-4 py-3 bg-gray-100 rounded-2xl rounded-tl-sm flex items-center gap-2">
                        <Loader className="w-4 h-4 text-primary animate-spin" />
                        <span className="text-sm text-gray-500">Génération de la réponse...</span>
                      </div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="mx-auto max-w-md p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 text-center">
                    {error}
                  </div>
                )}
              </>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Zone de saisie */}
          <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <div className="flex items-end gap-3">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Posez votre question… (Entrée pour envoyer)"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm disabled:opacity-60"
                rows={1}
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="bg-primary text-white p-3 rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              {credits && (
                <span className={credits.total < 1 ? 'text-red-600' : 'text-gray-500'}>
                  {credits.total} crédit{credits.total > 1 ? 's' : ''} IA restant{credits.total > 1 ? 's' : ''}
                  {credits.total < 1 && (
                    <>
                      {' — '}
                      <a href={LIEN_CREDITS} target="_blank" rel="noopener noreferrer" className="font-semibold underline">Obtenir plus de crédits</a>
                    </>
                  )}
                  {' · '}
                </span>
              )}
              L'IA peut faire des erreurs. Vérifiez les informations importantes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
